import {
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from "@shared/host/host-context";
import { readStorageItem, writeStorageItem } from "./storage";

const STORAGE_KEY = "shell.locale";

export { SUPPORTED_LOCALES, type SupportedLocale } from "@shared/host/host-context";

export const DEFAULT_LOCALE = SUPPORTED_LOCALES[0];

type StorageLike = Pick<Storage, "getItem" | "setItem">;

type DetectionSources = {
  storage?: StorageLike;
  navigatorLanguages?: readonly string[];
  navigatorLanguage?: string | null | undefined;
  documentLanguage?: string | null | undefined;
};

const normalizeCandidate = (candidate?: string | null) =>
  candidate?.toLowerCase().split(/[-_]/)[0];

export const resolveSupportedLocale = (candidate?: string | null) => {
  const normalized = normalizeCandidate(candidate);
  return SUPPORTED_LOCALES.includes(normalized as SupportedLocale)
    ? (normalized as SupportedLocale)
    : undefined;
};

const getBrowserStorage = () => {
  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
};

const getNavigatorLanguages = () => {
  if (typeof window === "undefined") {
    return undefined;
  }
  return window.navigator.languages;
};

const getNavigatorLanguage = () => {
  if (typeof window === "undefined") {
    return undefined;
  }
  return window.navigator.language;
};

const getDocumentLanguage = () => {
  if (typeof document === "undefined") {
    return undefined;
  }
  return (
    document.documentElement.lang ||
    document.documentElement.getAttribute("data-locale")
  );
};

export const detectInitialLocale = (
  sources: DetectionSources = {},
): SupportedLocale => {
  const storage = sources.storage ?? getBrowserStorage();
  const stored = (() => {
    if (!storage) {
      return readStorageItem(STORAGE_KEY);
    }

    try {
      return storage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  })();
  const storedLocale = resolveSupportedLocale(stored);
  if (storedLocale) {
    return storedLocale;
  }

  const documentLanguage = sources.documentLanguage ?? getDocumentLanguage();
  const documentLocale = resolveSupportedLocale(documentLanguage);
  if (documentLocale) {
    return documentLocale;
  }

  const languages = sources.navigatorLanguages ?? getNavigatorLanguages();
  if (languages) {
    for (const language of languages) {
      const supported = resolveSupportedLocale(language);
      if (supported) {
        return supported;
      }
    }
  }

  const primary = resolveSupportedLocale(
    sources.navigatorLanguage ?? getNavigatorLanguage(),
  );
  if (primary) {
    return primary;
  }

  return DEFAULT_LOCALE;
};

export const persistLocaleSelection = (
  locale: string,
  sources: { storage?: StorageLike } = {},
) => {
  const storage = sources.storage ?? getBrowserStorage();
  const supported = resolveSupportedLocale(locale);
  if (!supported) {
    return;
  }

  if (!storage && !sources.storage) {
    writeStorageItem(STORAGE_KEY, supported);
    return;
  }

  if (!storage) {
    return;
  }

  try {
    storage.setItem(STORAGE_KEY, supported);
  } catch {
    // ignore storage failures (e.g. private mode)
  }
};

export const getLocaleStorageKey = () => STORAGE_KEY;
